import React, { useState, useEffect } from 'react';
import { PlusCircle, X, Users } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AddButton from '../components/AddButton';
import EditButton from '../components/EditButton';
import SaveButton from '../components/SaveButton';
import BBALoader from '../components/BBALoader';
import { 
  saveOrgChartData, 
  loadOrgChartData, 
  addOrgChartEmployee, 
  updateOrgChartEmployee, 
  deleteOrgChartEmployee 
} from '../services/orgChartService';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import StartSidebar from '../components/StartSidebar';

// Function to get color based on grade
const getGradeColor = (grade) => {
  // A+ to A- are green shades
  if (grade === 'A+') return 'bg-green-500 text-white';
  if (grade === 'A') return 'bg-green-400 text-white';
  if (grade === 'A-') return 'bg-green-300 text-green-800';
  
  // B+ to B- are light green to yellow shades
  if (grade === 'B+') return 'bg-green-200 text-green-800';
  if (grade === 'B') return 'bg-yellow-200 text-yellow-800';
  if (grade === 'B-') return 'bg-yellow-300 text-yellow-800';
  
  // C+ to C- are yellow to orange shades
  if (grade === 'C+') return 'bg-yellow-400 text-yellow-800';
  if (grade === 'C') return 'bg-yellow-500 text-yellow-800';
  if (grade === 'C-') return 'bg-orange-300 text-orange-800';
  
  // D+ to D- are orange to red-orange shades
  if (grade === 'D+') return 'bg-orange-400 text-white';
  if (grade === 'D') return 'bg-orange-500 text-white';
  if (grade === 'D-') return 'bg-orange-600 text-white';
  
  // F is red
  if (grade === 'F') return 'bg-red-500 text-white';
  
  // Default
  return 'bg-gray-200 text-gray-800';
};

// Add these helper functions at the top with getGradeColor
const getTenureColor = (tenure) => {
  const years = parseFloat(tenure);
  if (years <= 0.5) return 'bg-red-100 text-red-800'; // Under 6 months
  if (years <= 1) return 'bg-yellow-100 text-yellow-800'; // Under 1 year
  if (years <= 3) return 'bg-green-100 text-green-800'; // Under 3 years
  if (years <= 5) return 'bg-green-300 text-green-800'; // Under 5 years
  return 'bg-green-500 text-white'; // 5+ years
};

const getSalaryColor = (salary) => {
  const amount = parseFloat(salary.replace(/[^0-9.-]+/g, ""));
  if (amount >= 100000) return 'bg-red-100 text-red-800';
  if (amount >= 75000) return 'bg-yellow-100 text-yellow-800';
  return 'bg-green-100 text-green-800';
};

const ExistingOrgChart = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    title: '',
    reportsTo: '',
    grade: 'C',
    tenureYears: '0',
    tenureMonths: '0',
    salary: '50000',
    headerColor: 'bg-red-500'
  });

  // Constants for layout calculations - base sizes that will be affected by zoom
  const cardWidth = 240; // Width of each card in pixels (w-60 = 15rem = 240px)
  const spacing = 64;    // Space between cards in pixels (space-x-16 = 4rem = 64px)

  // Calculate actual sizes based on zoom level
  const getZoomedSize = (size) => (size * zoomLevel) / 100;

  const grades = ['A+', 'A', 'A-', 'B+', 'B', 'B-', 'C+', 'C', 'C-', 'D+', 'D', 'D-', 'F'];

  // Helper function to format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    // Load org chart data when component mounts
    const loadData = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        const orgChartData = await loadOrgChartData();
        setEmployees(orgChartData.employees || []);
      } catch (error) {
        console.error('Error loading org chart data:', error);
        // Initialize with empty data if loading fails
        setEmployees([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currentUser]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'tenureYears' || name === 'tenureMonths' || name === 'salary') {
      // Ensure non-negative numbers
      const numValue = Math.max(0, parseInt(value) || 0);
      setFormData(prev => ({
        ...prev,
        [name]: numValue.toString(),
        // Calculate combined tenure if years or months change
        ...(name.startsWith('tenure') && {
          tenure: (
            parseFloat(name === 'tenureYears' ? numValue : prev.tenureYears) + 
            parseFloat(name === 'tenureMonths' ? numValue : prev.tenureMonths) / 12
          ).toFixed(2)
        })
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const saveEmployee = async () => {
    // Validate required fields
    if (!formData.name || !formData.title || !formData.grade || !formData.tenure || !formData.salary) {
      return;
    }
    
    // Special case for first employee or editing top-level employee
    const isFirstEmployee = employees.length === 0;
    const isTopLevelEmployee = formData.id && !employees.find(emp => emp.id === formData.id)?.reportsTo;
    
    // If it's not the first employee and not editing a top-level employee, reports to is required
    if (!isFirstEmployee && !isTopLevelEmployee && !formData.reportsTo) {
      alert("Please select who this member reports to.");
      return;
    }
    
    try {
      setSaving(true);
      
      const newEmployee = {
        ...formData,
        id: formData.id || Date.now().toString(),
        // For first employee or top-level employee, reportsTo should be null
        reportsTo: (isFirstEmployee || isTopLevelEmployee) ? null : formData.reportsTo
      };
      
      if (formData.id) {
        // Update existing employee
        await updateOrgChartEmployee(formData.id, newEmployee);
        const updatedEmployees = employees.map(emp => 
          emp.id === formData.id ? newEmployee : emp
        );
        setEmployees(updatedEmployees);
        
        // Auto-save the complete org chart data
        await saveOrgChartData({
          employees: updatedEmployees
        });
        
      } else {
        // Add new employee
        await addOrgChartEmployee(newEmployee);
        const updatedEmployees = [...employees, newEmployee];
        setEmployees(updatedEmployees);
        
        // Auto-save the complete org chart data
        await saveOrgChartData({
          employees: updatedEmployees
        });
        
      }
      
      resetForm();
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error saving employee:', error);
      setMessage({ type: 'error', text: 'Failed to save employee. Please try again.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      id: '',
      name: '',
      title: '',
      reportsTo: '',
      grade: 'C',
      tenureYears: '0',
      tenureMonths: '0',
      salary: '50000',
      headerColor: 'bg-red-500'
    });
  };

  const editEmployee = (employee) => {
    const tenureValue = parseFloat(employee.tenure);
    const tenureYears = Math.floor(tenureValue);
    const tenureMonths = Math.round((tenureValue - tenureYears) * 12);
    
    setFormData({
      ...employee,
      tenureYears: tenureYears.toString(),
      tenureMonths: tenureMonths.toString(),
      salary: employee.salary.replace(/[^0-9.-]+/g, "")
    });
    setIsModalOpen(true);
  };

  const deleteEmployee = async (id) => {
    try {
      setSaving(true);
      await deleteOrgChartEmployee(id);
      
      // Update local state
      const updatedEmployees = employees.filter(emp => emp.id !== id);
      setEmployees(updatedEmployees);
      
      // Auto-save the complete org chart data
      await saveOrgChartData({
        employees: updatedEmployees
      });
      
      
      // Close confirmation modal and reset form
      setShowDeleteConfirm(false);
      setEmployeeToDelete(null);
      setIsModalOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error deleting employee:', error);
      setMessage({ type: 'error', text: 'Failed to delete employee. Please try again.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (employee) => {
    setEmployeeToDelete(employee);
    setShowDeleteConfirm(true);
  };

  const getDirectReports = (managerId) => {
    return employees.filter(emp => emp.reportsTo === managerId);
  };

  // Add these new functions for drag functionality
  const handleMouseDown = (e) => {
    // Only start dragging if not clicking on a card or button
    if (e.target.closest('.employee-card') || e.target.closest('button')) {
      return;
    }
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Add event listeners for drag functionality
  useEffect(() => {
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // Add recenter function
  const handleRecenter = () => {
    setPosition({ x: 0, y: 0 });
  };

  // Render employee card
  const EmployeeCard = ({ employee }) => {
    const zoomedWidth = getZoomedSize(cardWidth);
    
    // Format tenure to show years
    const formatTenure = (tenure) => {
      const years = parseFloat(tenure);
      return `${years} ${years === 1 ? 'Year' : 'Years'}`;
    };

    // Format salary to show as currency
    const formatSalary = (salary) => {
      const amount = parseFloat(salary.replace(/[^0-9.-]+/g, ""));
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0
      }).format(amount);
    };
    
    return (
      <div 
        className={`employee-card rounded-lg shadow-md overflow-hidden border transition-shadow ${
          darkMode ? 'border-gray-600 bg-gray-800' : 'border-gray-200 bg-white'
        } cursor-pointer hover:shadow-lg`}
        style={{ width: `${zoomedWidth}px`, fontSize: `${zoomLevel/100}rem` }}
        onClick={() => editEmployee(employee)}
      >          
        {/* Header with name and title */}
        <div className={`${employee.headerColor || 'bg-red-500'} w-full py-2 px-3 text-center text-white`}>
          <h3 className="font-medium">{employee.name}</h3>
          <p className="text-xs">{employee.title}</p>
        </div>
        
        {/* Card body */}
        <div className="p-3 flex items-center">
          {/* Profile placeholder */}
          <div className="mr-3">
            <div 
              className="rounded-full bg-gray-200 flex items-center justify-center"
              style={{ 
                width: `${getZoomedSize(64)}px`, 
                height: `${getZoomedSize(64)}px` 
              }}
            >
              <Users size={getZoomedSize(24)} className="text-gray-400" />
            </div>
          </div>
          
          {/* Employee details */}
          <div className="flex-1 space-y-2">
            <div className={`px-2 py-1 ${getGradeColor(employee.grade)} rounded text-sm w-full text-center`}>
              Grade: {employee.grade}
            </div>
            <div className={`px-2 py-1 ${getTenureColor(employee.tenure)} rounded text-sm w-full text-center`}>
              {formatTenure(employee.tenure)}
            </div>
            <div className={`px-2 py-1 ${getSalaryColor(employee.salary)} rounded text-sm w-full text-center`}>
              {formatSalary(employee.salary)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Single direct report branch
  const SingleReportBranch = ({ employee, employees: chartEmployees = employees }) => {
    const subReports = chartEmployees.filter(emp => emp.reportsTo === employee.id);
    const branchWidth = getZoomedSize(calculateBranchWidth(employee.id, chartEmployees));
    
    return (
      <div className="flex flex-col items-center">
        <div 
          style={{
            width: '4px',
            height: `${getZoomedSize(20)}px`,
            backgroundColor: '#9CA3AF',
            margin: '0 auto'
          }}
        />
                  <div className="relative" style={{ 
            width: `${branchWidth}px`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            <div style={{ width: `${getZoomedSize(cardWidth)}px` }}>
              <EmployeeCard employee={employee} />
            </div>
            <NestedReports employeeId={employee.id} employees={chartEmployees} />
          </div>
      </div>
    );
  };

  // Helper function to calculate required width for a branch
  const calculateBranchWidth = (employeeId, chartEmployees = employees) => {
    const reports = chartEmployees.filter(emp => emp.reportsTo === employeeId);
    if (reports.length === 0) return cardWidth;
    if (reports.length === 1) {
      const subWidth = calculateBranchWidth(reports[0].id, chartEmployees);
      return Math.max(cardWidth, subWidth);
    }
    
    // Calculate width needed for each report's branch
    const reportWidths = reports.map(report => {
      const subReports = chartEmployees.filter(emp => emp.reportsTo === report.id);
      if (subReports.length === 0) return cardWidth;
      
      // Calculate width for children while maintaining parent's position
      const subWidth = calculateBranchWidth(report.id, chartEmployees);
      return Math.max(cardWidth, subWidth);
    });
    
    // Total width needed is sum of all report widths plus spacing between them
    return reportWidths.reduce((sum, width) => sum + width, 0) + 
           ((reports.length - 1) * spacing);
  };

  // Multiple direct reports branch with dynamic spacing
  const MultipleReportsBranch = ({ reports, employees: chartEmployees = employees }) => {
    const zoomedSpacing = getZoomedSize(spacing);
    const zoomedCardWidth = getZoomedSize(cardWidth);
    
    // Calculate branch widths for immediate children
    const childBranchWidths = reports.map(report => {
      const subReports = chartEmployees.filter(emp => emp.reportsTo === report.id);
      return getZoomedSize(
        subReports.length > 0 
          ? calculateBranchWidth(report.id, chartEmployees)
          : cardWidth
      );
    });
    
    // Calculate width for current row based on actual branch widths
    const currentRowWidth = childBranchWidths.reduce((sum, width) => sum + width, 0) + 
                          (zoomedSpacing * (reports.length - 1));
    
    return (
      <div className="flex flex-col items-center">
        {/* Vertical line from parent to horizontal connector */}
        <div 
          style={{
            width: '4px',
            height: `${getZoomedSize(20)}px`,
            backgroundColor: '#9CA3AF',
            margin: '0 auto'
          }}
        />
        
        {/* Container for this level */}
        <div className="relative" style={{ width: `${currentRowWidth}px` }}>
          {/* Horizontal connector line - only show if more than one report */}
          {reports.length > 1 && (() => {
            // Calculate the center positions of the first and last children
            let currentPosition = 0;
            const firstChildCenter = currentPosition + (childBranchWidths[0] / 2);
            
            currentPosition += childBranchWidths[0] + zoomedSpacing;
            for (let i = 1; i < childBranchWidths.length - 1; i++) {
              currentPosition += childBranchWidths[i] + zoomedSpacing;
            }
            const lastChildCenter = currentPosition + (childBranchWidths[childBranchWidths.length - 1] / 2);
            
            const lineWidth = lastChildCenter - firstChildCenter;
            const lineLeft = firstChildCenter;
            
            return (
              <div 
                className="absolute" 
                style={{
                  top: '0px',
                  left: `${lineLeft}px`,
                  width: `${lineWidth}px`,
                  height: '4px',
                  backgroundColor: '#9CA3AF',
                  zIndex: 1
                }}
              />
            );
          })()}
          
          {/* Reports row with dynamic spacing */}
          <div className="flex justify-center" style={{ gap: `${zoomedSpacing}px` }}>
            {reports.map((report, index) => {
              const subReports = chartEmployees.filter(emp => emp.reportsTo === report.id);
              const branchWidth = childBranchWidths[index];
              
              return (
                <div 
                  key={report.id} 
                  className="relative flex flex-col items-center"
                  style={{ 
                    width: `${branchWidth}px`
                  }}
                >
                  {/* Vertical line from horizontal connector to employee */}
                  <div 
                    style={{
                      width: '4px',
                      height: `${getZoomedSize(20)}px`,
                      backgroundColor: '#9CA3AF',
                      margin: '0 auto'
                    }}
                  />
                  <div className="relative flex flex-col items-center">
                    <div style={{ width: `${zoomedCardWidth}px` }}>
                      <EmployeeCard employee={report} />
                    </div>
                    <NestedReports employeeId={report.id} employees={chartEmployees} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Component to handle direct reports display
  const NestedReports = ({ employeeId, employees: chartEmployees = employees }) => {
    const reports = chartEmployees.filter(emp => emp.reportsTo === employeeId);
    
    console.log(`NestedReports for ${employeeId}:`, reports);
    
    if (reports.length === 0) {
      return null;
    }
    
    if (reports.length === 1) {
      return <SingleReportBranch employee={reports[0]} employees={chartEmployees} />;
    }
    
    return <MultipleReportsBranch reports={reports} employees={chartEmployees} />;
  };

  // Organization chart component
  const OrganizationChart = ({ employees: chartEmployees = employees }) => {
    // Find all top-level employees (those with no manager)
    const topLevelEmployees = chartEmployees.filter(emp => !emp.reportsTo);
    
    console.log('OrganizationChart - All employees:', chartEmployees);
    console.log('OrganizationChart - Top level employees:', topLevelEmployees);
    
    if (topLevelEmployees.length === 0) {
      return (
        <div className={`text-center py-16 px-8 rounded-lg border border-dashed ${
          darkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-300'
        }`}>
          <Users size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>No members added yet</h3>
          <p className={`mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Add members to start building your organizational chart</p>
          <div className="flex justify-center">
            <AddButton
              onClick={() => setIsModalOpen(true)}
              className="!w-auto !h-auto px-4 py-2 rounded-lg"
            >
              <span className="ml-2">Add Your First Member</span>
            </AddButton>
          </div>
        </div>
      );
    }
    
    // For a single top-level employee
    if (topLevelEmployees.length === 1) {
      return (
        <div className="flex flex-col items-center pt-8">
          <div className="relative" style={{ width: '240px' }}>
            <EmployeeCard employee={topLevelEmployees[0]} />
            <NestedReports employeeId={topLevelEmployees[0].id} employees={chartEmployees} />
          </div>
        </div>
      );
    }
    
    // For multiple top-level employees (e.g., Board of Directors)
    const totalWidth = (topLevelEmployees.length - 1) * (cardWidth + spacing);
    const startOffset = totalWidth / 2;

    return (
      <div className="flex flex-col items-center pt-4">
        <div className={`mb-8 px-8 py-2 rounded-md text-sm font-medium ${
          darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-800'
        }`}>
          Board of Directors
        </div>
        
        <div className="relative" style={{ width: `${totalWidth + cardWidth}px` }}>
          {/* Horizontal connector line */}
          <div 
            className="absolute" 
            style={{
              top: '50%',
              left: `calc(50% - ${startOffset}px)`,
              width: `${totalWidth}px`,
              height: '4px',
              backgroundColor: '#9CA3AF',
              zIndex: 1
            }}
          />
          
          {/* Render each top-level employee */}
          <div className="flex justify-between">
            {topLevelEmployees.map((employee) => (
              <div key={employee.id} className="relative flex flex-col items-center">
                <EmployeeCard employee={employee} />
                <NestedReports employeeId={employee.id} employees={chartEmployees} />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <StartSidebar />
      <div className="w-full p-4">
        {/* Header section */}
        <div className="flex items-center justify-center mb-4">
          <h1 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Existing Org Chart</h1>
        </div>

      {/* Message display */}
      {message.text && (
        <div className={`mb-4 p-3 rounded-md ${
          message.type === 'success' 
            ? 'bg-green-100 text-green-800 border border-green-200' 
            : 'bg-red-100 text-red-800 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* Main content */}
      {loading ? (
        <div className="flex justify-center items-center py-16">
          <BBALoader size={128} />
        </div>
      ) : (
        <>
          <div className="flex justify-between items-center mb-6">
            <div className={`flex items-center gap-8 text-xl ${darkMode ? 'text-white' : 'text-black'}`}>
              <div className="flex items-center gap-3">
                <span className="font-semibold">Headcount:</span>
                <span>{employees.length}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-semibold">Total Salary:</span>
                <span>
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    maximumFractionDigits: 0
                  }).format(
                    employees.reduce((total, emp) => 
                      total + parseFloat(emp.salary.replace(/[^0-9.-]+/g, "")), 0
                    )
                  )}
                </span>
              </div>
            </div>

            {/* Save and Continue Button - Centered */}
            <div className="flex justify-center">
              <button
                onClick={async () => {
                  try {
                    // Save org chart completion status to Firestore
                    if (currentUser) {
                      await updateDoc(doc(db, 'users', currentUser.uid), {
                        'businessAssessment.orgChartCompleted': true,
                        'businessAssessmentProgress.orgChartCompleted': true,
                        'operationsData.orgChartCompleted': true,
                        lastUpdated: new Date().toISOString()
                      });
                      
                    }
                    
                    // Navigate back to existing conditions page with state to indicate we came from org chart
                    navigate('/start/existing-conditions', { state: { fromExistingOrgChart: true } });
                  } catch (error) {
                    console.error('Error saving org chart completion:', error);
                    alert('Error saving data. Please try again.');
                  }
                }}
                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Save and Continue
              </button>
            </div>

            <div className="flex items-center gap-4">
              <button
                onClick={handleRecenter}
                className="px-3 py-1 bg-gray-100 rounded hover:bg-gray-200 flex items-center gap-2"
                title="Recenter chart"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="16"/>
                  <line x1="8" y1="12" x2="16" y2="12"/>
                </svg>
                Recenter
              </button>
              {/* Zoom controls */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setZoomLevel(Math.max(50, zoomLevel - 1))}
                  className={`px-2 py-1 rounded ${
                    darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                  }`}
                >
                  -
                </button>
                <select
                  value={zoomLevel}
                  onChange={(e) => setZoomLevel(parseInt(e.target.value))}
                  className={`px-3 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-gray-300' : 'border-gray-300 bg-white text-gray-900'
                  }`}
                >
                  <option value={zoomLevel}>{zoomLevel}%</option>
                  <option value="100">100%</option>
                  <option value="90">90%</option>
                  <option value="80">80%</option>
                  <option value="70">70%</option>
                  <option value="60">60%</option>
                  <option value="50">50%</option>
                </select>
                <button
                  onClick={() => setZoomLevel(Math.min(100, zoomLevel + 1))}
                  className={`px-2 py-1 rounded ${
                    darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                  }`}
                >
                  +
                </button>
              </div>
              <AddButton
                onClick={() => {
                  resetForm();
                  setIsModalOpen(true);
                }}
                className="!w-auto !h-auto px-4 py-2 rounded-lg"
              >
                <span className="ml-2">Add Member</span>
              </AddButton>
            </div>
          </div>

          <div 
            className="w-full flex justify-center overflow-hidden cursor-grab active:cursor-grabbing"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <div 
              className="transform-origin-top transition-transform duration-100"
              style={{ 
                transform: `scale(${zoomLevel/100}) translate(${position.x}px, ${position.y}px)`,
                cursor: isDragging ? 'grabbing' : 'grab'
              }}
            >
              <OrganizationChart />
            </div>
          </div>
        </>
      )}

      {/* Add/Edit Employee Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg p-6 w-full max-w-lg max-h-screen overflow-y-auto`}>
            <div className="flex justify-between items-center mb-4">
              <div className="w-6"></div>
              <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {formData.id ? 'Edit Member' : 'Add Member'}
              </h2>
              <button 
                onClick={() => {
                  setIsModalOpen(false);
                  resetForm();
                }}
                className={`${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className={`block mb-1 font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300 bg-white text-gray-900'
                  }`}
                  required
                />
              </div>

              <div>
                <label className={`block mb-1 font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter job title..."
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300 bg-white text-gray-900'
                  }`}
                  required
                />
                <div className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Common titles: Owner, Operations Director, Project Manager, Carpenter, Designer, etc.
                </div>
              </div>

              <div>
                <label className={`block mb-1 font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Reports To *</label>
                <select
                  name="reportsTo"
                  value={formData.reportsTo || ''}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300 bg-white text-gray-900'
                  }`}
                  required
                >
                  <option value="">Select Manager</option>
                  {employees
                    .filter(emp => emp.id !== formData.id)
                    .map(emp => (
                      <option key={emp.id} value={emp.id}>
                        {emp.name} - {emp.title}
                      </option>
                    ))
                  }
                </select>
              </div>

              <div>
                <label className={`block mb-1 font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Grade *</label>
                <select
                  name="grade"
                  value={formData.grade}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300 bg-white text-gray-900'
                  }`}
                  required
                >
                  {grades.map(grade => (
                    <option key={grade} value={grade}>{grade}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`block mb-1 font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Tenure</label>
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Years</label>
                    <input
                      type="number"
                      name="tenureYears"
                      value={formData.tenureYears}
                      onChange={handleInputChange}
                      min="0"
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                        darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300 bg-white text-gray-900'
                      }`}
                    />
                  </div>
                  <div className="flex-1">
                    <label className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Months</label>
                    <input
                      type="number"
                      name="tenureMonths"
                      value={formData.tenureMonths}
                      onChange={handleInputChange}
                      min="0"
                      max="11"
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                        darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300 bg-white text-gray-900'
                      }`}
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className={`block mb-1 font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Salary</label>
                <div className="flex items-center">
                  <span className={`mr-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>$</span>
                  <input
                    type="number"
                    name="salary"
                    value={formData.salary}
                    onChange={handleInputChange}
                    min="0"
                    step="1000"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300 bg-white text-gray-900'
                    }`}
                  />
                </div>
              </div>

              <div>
                <label className={`block mb-1 font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Header Color</label>
                <select
                  name="headerColor"
                  value={formData.headerColor}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300 bg-white text-gray-900'
                  }`}
                >
                  <option value="bg-red-500">Red</option>
                  <option value="bg-blue-500">Blue</option>
                  <option value="bg-green-500">Green</option>
                  <option value="bg-purple-500">Purple</option>
                  <option value="bg-indigo-500">Indigo</option>
                  <option value="bg-pink-500">Pink</option>
                  <option value="bg-yellow-500">Yellow</option>
                  <option value="bg-gray-500">Gray</option>
                </select>
              </div>

              <div className="flex justify-between gap-3 pt-4">
                <div className="flex gap-3">
                  {formData.id && (
                    <button
                      onClick={() => handleDeleteClick(formData)}
                      disabled={saving}
                      className={`px-4 py-2 rounded-md hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                        darkMode ? 'bg-red-900 text-red-200 hover:bg-red-800' : 'bg-red-100 text-red-700'
                      }`}
                    >
                      Delete Member
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setIsModalOpen(false);
                      resetForm();
                    }}
                    className={`px-4 py-2 rounded-md ${
                      darkMode ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Cancel
                  </button>
                </div>
                <SaveButton
                  onClick={() => {
                    saveEmployee();
                  }}
                  disabled={!formData.name || !formData.title || !formData.grade || saving}
                  className="!w-auto !h-auto px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white disabled:bg-red-300 disabled:cursor-not-allowed"
                >
                  <span className="ml-2">{saving ? 'Saving...' : (formData.id ? 'Update' : 'Add')}</span>
                </SaveButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && employeeToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg p-6 w-full max-w-md`}>
            <div className="flex justify-between items-center mb-4">
              <div className="w-6"></div>
              <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Delete Member
              </h2>
              <button 
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setEmployeeToDelete(null);
                }}
                className={`${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <X size={24} />
              </button>
            </div>

            <div className="mb-6">
              <p className={`${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-4`}>
                Are you sure you want to delete <strong>{employeeToDelete.name}</strong> ({employeeToDelete.title})?
              </p>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                This action cannot be undone. All reporting relationships will be updated automatically.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setEmployeeToDelete(null);
                }}
                className={`px-4 py-2 rounded-md ${
                  darkMode ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={() => deleteEmployee(employeeToDelete.id)}
                disabled={saving}
                className={`px-4 py-2 rounded-md ${
                  saving 
                    ? 'bg-red-300 text-white cursor-not-allowed' 
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                {saving ? 'Deleting...' : 'Delete Member'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default ExistingOrgChart;
